import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { UserBadge } from '../components/UserBadge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Plus, Loader2, CheckSquare, Circle, LayoutGrid, List, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const PRIORITY_CONFIG = {
  low: { label: 'Bassa', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  medium: { label: 'Media', color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' },
  high: { label: 'Alta', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' },
  urgent: { label: 'Urgente', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
};

const STATUS_COLUMNS = [
  { key: 'todo', label: 'Da Fare', color: '#6366f1' },
  { key: 'in_progress', label: 'In Corso', color: '#f59e0b' },
  { key: 'completed', label: 'Completate', color: '#22c55e' },
];

const TYPES = [
  {value:'todo', label:'Task'},
  {value:'call', label:'Chiamata'},
  {value:'email', label:'Email'},
  {value:'followup', label:'Follow-up'},
  {value:'meeting_prep', label:'Prep. Riunione'},
];

function ActivityCard({ act, onComplete, onDelete }) {
  const pCfg = PRIORITY_CONFIG[act.priority] || PRIORITY_CONFIG.medium;
  const isCompleted = act.status === 'completed';
  const isOverdue = act.due_date && new Date(act.due_date) < new Date() && !isCompleted;
  const { canDelete } = usePermissions();

  return (
    <div className={`bg-card border border-border rounded-lg p-3 hover:shadow-sm transition-all ${isCompleted ? 'opacity-60' : ''}`} data-testid={`activity-card-${act._id || act.id}`}>
      <div className="flex items-start gap-2">
        <button
          onClick={() => !isCompleted && onComplete(act._id || act.id)}
          className="mt-0.5 flex-shrink-0"
          data-testid={`complete-activity-${act._id || act.id}`}
        >
          {isCompleted
            ? <CheckSquare size={16} className="text-green-500" />
            : <Circle size={16} className="text-muted-foreground hover:text-primary transition-colors" />
          }
        </button>
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium leading-snug ${isCompleted ? 'line-through text-muted-foreground' : ''}`}>{act.title}</p>
          {act.description && <p className="text-xs text-muted-foreground mt-0.5 truncate">{act.description}</p>}
          <div className="flex items-center gap-1.5 mt-1.5">
            <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${pCfg.color}`}>{pCfg.label}</span>
            {act.due_date && (
              <span className={`text-xs ${isOverdue ? 'text-red-500 font-medium' : 'text-muted-foreground'}`}>
                {new Date(act.due_date).toLocaleDateString('it-IT')}
              </span>
            )}
            {act.assigned_to && <UserBadge userId={act.assigned_to} size="xs" />}
          </div>
        </div>
        {canDelete('activities') && (
          <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive opacity-0 group-hover:opacity-100" onClick={() => onDelete(act._id || act.id, act.title)}>
            <Trash2 size={11} />
          </Button>
        )}
      </div>
    </div>
  );
}

export default function Activities() {
  const { canCreate, canDelete } = usePermissions();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list');
  const [filter, setFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ type:'todo', title:'', description:'', priority:'medium', due_date:'', status:'todo' });

  const fetchActivities = async () => {
    try {
      const res = await axios.get(`${API}/activities`);
      setActivities(res.data);
    } catch { toast.error('Errore caricamento'); } finally { setLoading(false); }
  };

  useEffect(() => { fetchActivities(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.due_date) delete payload.due_date;
      await axios.post(`${API}/activities`, payload);
      toast.success('Attività creata');
      setShowForm(false);
      setForm({ type:'todo', title:'', description:'', priority:'medium', due_date:'', status:'todo' });
      fetchActivities();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); } finally { setSaving(false); }
  };

  const handleComplete = async (id) => {
    try {
      await axios.patch(`${API}/activities/${id}/complete`);
      toast.success('Attività completata!');
      setActivities(prev => prev.map(a => (a._id === id || a.id === id) ? { ...a, status: 'completed' } : a));
    } catch { toast.error('Errore'); }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Eliminare "${title}"?`)) return;
    try {
      await axios.delete(`${API}/activities/${id}`);
      toast.success('Attività eliminata');
      setActivities(prev => prev.filter(a => a._id !== id && a.id !== id));
    } catch { toast.error('Errore'); }
  };

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const filteredActivities = activities.filter(a => {
    if (filter === 'all') return true;
    if (filter === 'todo') return a.status !== 'completed' && a.status !== 'cancelled';
    if (filter === 'completed') return a.status === 'completed';
    if (filter === 'overdue') return a.due_date && new Date(a.due_date) < new Date() && a.status !== 'completed';
    return true;
  });

  const stats = {
    total: activities.length,
    todo: activities.filter(a => a.status === 'todo').length,
    in_progress: activities.filter(a => a.status === 'in_progress').length,
    completed: activities.filter(a => a.status === 'completed').length,
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{fontFamily:'Manrope, sans-serif'}}>Attività e Task</h1>
          <p className="text-muted-foreground text-sm mt-1">{stats.todo} in sospeso • {stats.completed} completate</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex border border-border rounded-lg overflow-hidden">
            <Button variant={view === 'list' ? 'default' : 'ghost'} size="sm" className="rounded-none" onClick={() => setView('list')} data-testid="activities-list-btn">
              <List size={14} className="mr-1" />Lista
            </Button>
            <Button variant={view === 'kanban' ? 'default' : 'ghost'} size="sm" className="rounded-none" onClick={() => setView('kanban')} data-testid="activities-kanban-btn">
              <LayoutGrid size={14} className="mr-1" />Kanban
            </Button>
          </div>
          {canCreate('activities') && (
            <Button onClick={() => setShowForm(true)} data-testid="add-activity-btn">
              <Plus size={16} className="mr-2" />Nuova Attività
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { key: 'all', label: 'Totale', count: stats.total },
          { key: 'todo', label: 'Da fare', count: stats.todo },
          { key: 'in_progress', label: 'In corso', count: stats.in_progress },
          { key: 'completed', label: 'Completate', count: stats.completed },
        ].map(({ key, label, count }) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            className={`text-center p-3 rounded-lg border transition-all ${filter === key ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50'}`}
            data-testid={`filter-${key}`}
          >
            <p className="text-xl font-bold" style={{fontFamily:'Manrope, sans-serif'}}>{count}</p>
            <p className="text-xs text-muted-foreground">{label}</p>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin text-primary" size={28} /></div>
      ) : view === 'list' ? (
        <div className="space-y-2">
          {filteredActivities.length === 0 ? (
            <div className="text-center py-12">
              <CheckSquare size={40} className="text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">Nessuna attività</p>
              {canCreate('activities') && (
                <Button className="mt-3" onClick={() => setShowForm(true)}><Plus size={14} className="mr-2" />Aggiungi</Button>
              )}
            </div>
          ) : (
            filteredActivities.map(act => (
              <div key={act._id || act.id} className="group">
                <ActivityCard act={act} onComplete={handleComplete} onDelete={handleDelete} />
              </div>
            ))
          )}
        </div>
      ) : (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {STATUS_COLUMNS.map(col => {
            const colActivities = activities.filter(a => a.status === col.key);
            return (
              <div key={col.key} className="flex flex-col bg-muted/30 rounded-xl min-w-[260px] w-72">
                <div className="p-3 border-b border-border">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full" style={{ background: col.color }} />
                      <span className="text-sm font-semibold" style={{fontFamily:'Manrope, sans-serif'}}>{col.label}</span>
                    </div>
                    <Badge variant="outline" className="text-xs">{colActivities.length}</Badge>
                  </div>
                </div>
                <div className="flex-1 p-2 space-y-2 overflow-y-auto" style={{ maxHeight: '60vh' }}>
                  {colActivities.map(act => (
                    <div key={act._id || act.id} className="group">
                      <ActivityCard act={act} onComplete={handleComplete} onDelete={handleDelete} />
                    </div>
                  ))}
                  {colActivities.length === 0 && (
                    <p className="text-center text-xs text-muted-foreground py-4">Nessuna attività</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle style={{fontFamily:'Manrope, sans-serif'}}>Nuova Attività</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4 mt-2">
            <div className="space-y-1.5">
              <Label>Titolo *</Label>
              <Input value={form.title} onChange={e => setField('title', e.target.value)} required data-testid="activity-title-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Tipo</Label>
                <Select value={form.type} onValueChange={v => setField('type', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Priorità</Label>
                <Select value={form.priority} onValueChange={v => setField('priority', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(PRIORITY_CONFIG).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Scadenza</Label>
              <Input type="datetime-local" value={form.due_date} onChange={e => setField('due_date', e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Descrizione</Label>
              <Textarea value={form.description} onChange={e => setField('description', e.target.value)} rows={2} />
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setShowForm(false)}>Annulla</Button>
              <Button type="submit" className="flex-1" disabled={saving} data-testid="save-activity-btn">
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Salva
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
